import { useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { router, usePage } from "@inertiajs/react";
import { Search, List, CornerDownLeft, ArrowUp, ArrowDown, Monitor } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type PageItem = {
  id: string;
  label: string;
  path: string;
  group?: string;
  icon?: string;
};

interface PageProps {
  awesomebar?: {
    pages?: PageItem[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface Props {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function AwesomeBar({ open: openProp, onOpenChange }: Props) {
  const { awesomebar } = usePage<PageProps>().props;
  const pages = useMemo(() => awesomebar?.pages || [], [awesomebar?.pages]);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? openProp : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setQuery("");
      setActiveIndex(0);
    }
    setOpen(nextOpen);
  };

  useHotkeys("mod+k", () => handleOpenChange(true), {
    preventDefault: true,
    enableOnFormTags: true,
    enableOnContentEditable: true,
  });

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return pages;
    return pages.filter((page) => page.label.toLowerCase().includes(term));
  }, [pages, query]);

  const active = filtered[activeIndex];

  const move = (direction: "up" | "down") => {
    setActiveIndex((prev) => {
      if (filtered.length === 0) return 0;
      const next = direction === "up" ? prev - 1 : prev + 1;
      if (next < 0) return filtered.length - 1;
      if (next >= filtered.length) return 0;
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="text-base">البحث عن الصفحات</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4">
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  move("down");
                }
                if (event.key === "ArrowUp") {
                  event.preventDefault();
                  move("up");
                }
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (active) {
                    handleOpenChange(false);
                    router.visit(active.path);
                  }
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  handleOpenChange(false);
                }
              }}
              placeholder="ابحث أو اكتب أمرًا"
              className="w-full border-none bg-transparent text-sm outline-none focus-visible:outline-none focus-visible:ring-0"
            />
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto px-2 pb-4">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">لا توجد نتائج.</div>
          ) : (
            <div className="space-y-1">
              {filtered.map((page, index) => {
                const Icon = page.icon === "monitor" ? Monitor : List;
                return (
                  <button
                    key={page.id}
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      handleOpenChange(false);
                      router.visit(page.path);
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-4 py-2 text-start text-sm transition",
                      index === activeIndex ? "bg-muted" : "hover:bg-muted/60"
                    )}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1">{page.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />
              <ArrowDown className="h-3 w-3" />
              للتنقل
            </span>
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" />
              للاختيار
            </span>
          </div>
          <span>Esc للإغلاق</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
